/**
 * useManagePurchases — ViewModel for /manage/purchases (bare purchase
 * ledger; writes to the existing `purchases` table without satellite
 * links).
 */

import { useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import type { ManagePurchaseDto } from '../models/manage-purchase.model'
import type { UnitDto } from '../models/unit.model'
import { mutateJson } from '../utils/api/mutateJson'
import { parseApiError } from '../utils/api/parseApiError'
import { usePaginatedList } from './usePaginatedList'

export type { ManagePurchaseDto } from '../models/manage-purchase.model'
export type { UnitDto } from '../models/unit.model'

export interface SupplierRef {
  readonly id: string
  readonly name: string
}

export interface CreateManagePurchaseInput {
  readonly invoice_no: string
  readonly supplier_id: string
  readonly quantity_millis: number
  readonly unit_id: string
  readonly total_paid_paisa: number
  readonly cgst_amount_paisa: number
  readonly sgst_amount_paisa: number
  readonly purchase_date: string
  readonly billed_date: string
  readonly notes: string | null
}

export interface UpdateManagePurchaseInput {
  readonly invoice_no?: string
  readonly supplier_id?: string
  readonly quantity_millis?: number
  readonly unit_id?: string
  readonly total_paid_paisa?: number
  readonly cgst_amount_paisa?: number
  readonly sgst_amount_paisa?: number
  readonly purchase_date?: string
  readonly billed_date?: string
  readonly notes?: string | null
}

export interface BillUploadInput {
  readonly filename: string
  readonly content_type: string
  readonly data_base64: string
}

export interface BillDownload {
  readonly content_type: string
  readonly data_base64: string
}

export interface UseManagePurchasesReturn {
  readonly items: ManagePurchaseDto[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  /** Current GST split — CGST + SGST in basis points (900 + 900 = 18%
   *  total). Both null until the operator adds + marks one current on
   *  /manage/gst-rates. */
  readonly currentGstCgstBps: number | null
  readonly currentGstSgstBps: number | null
  /** Units catalogue for the form-modal dropdown. */
  readonly units: UnitDto[]
  /** Active suppliers catalogue for the form-modal supplier dropdown
   *  (all types — raw_material + artisan). */
  readonly suppliers: SupplierRef[]
  readonly setPage: (n: number) => void
  readonly reload: () => void
  readonly create: (
    input: CreateManagePurchaseInput
  ) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly update: (
    id: string,
    patch: UpdateManagePurchaseInput
  ) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly remove: (id: string) => Promise<{ ok: true } | { ok: false; message: string }>
  readonly getBill: (id: string) => Promise<{ ok: true; data: BillDownload } | { ok: false; message: string }>
  readonly uploadBill: (
    id: string,
    input: BillUploadInput
  ) => Promise<{ ok: true; data: ManagePurchaseDto } | { ok: false; field: string | null; message: string }>
  readonly deleteBill: (id: string) => Promise<{ ok: true; data: ManagePurchaseDto } | { ok: false; message: string }>
}

interface ListExtras {
  readonly currentGstCgstBps: number | null
  readonly currentGstSgstBps: number | null
  readonly units: UnitDto[]
  readonly suppliers: SupplierRef[]
}

interface ListBody {
  readonly success: true
  readonly data: {
    readonly items: ManagePurchaseDto[]
    readonly total: number
    readonly page: number
    readonly pageSize: number
    readonly currentGstCgstBps: number | null
    readonly currentGstSgstBps: number | null
    readonly units: UnitDto[]
    readonly suppliers: SupplierRef[]
  }
}

const EMPTY_EXTRAS: ListExtras = { currentGstCgstBps: null, currentGstSgstBps: null, units: [], suppliers: [] }

export function useManagePurchases(): UseManagePurchasesReturn {
  const { authedFetch } = useAuth()

  const paged = usePaginatedList<ManagePurchaseDto, ListExtras>({
    fetcher: useCallback(
      async (page, pageSize, signal) => {
        const url = `${APIROUTES.MANAGEPURCHASES}?page=${page}&pageSize=${pageSize}`
        const res = await authedFetch(url, { signal })
        if (!res.ok) {
          const { message } = await parseApiError(res)
          throw new Error(message)
        }
        const body = (await res.json()) as ListBody
        return {
          items: body.data.items,
          total: body.data.total,
          extras: {
            currentGstCgstBps: body.data.currentGstCgstBps,
            currentGstSgstBps: body.data.currentGstSgstBps,
            units: body.data.units,
            suppliers: body.data.suppliers ?? []
          }
        }
      },
      [authedFetch]
    )
  })

  const create = useCallback(
    async (input: CreateManagePurchaseInput) => {
      const r = await mutateJson<ManagePurchaseDto>(
        authedFetch,
        APIROUTES.MANAGEPURCHASES,
        'POST',
        input
      )
      if (!r.ok) return r
      if (paged.page === 1) paged.insertItem(r.data)
      else paged.reload()
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  const update = useCallback(
    async (id: string, patch: UpdateManagePurchaseInput) => {
      const r = await mutateJson<ManagePurchaseDto>(
        authedFetch,
        fillPath(APIROUTES.MANAGEPURCHASEITEM, { id }),
        'PATCH',
        patch
      )
      if (!r.ok) return r
      paged.updateItem((it) => it.id === id, r.data)
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  const remove = useCallback(
    async (id: string) => {
      const r = await mutateJson<{ ok: true }>(
        authedFetch,
        fillPath(APIROUTES.MANAGEPURCHASEITEM, { id }),
        'DELETE'
      )
      if (!r.ok) return { ok: false as const, message: r.message }
      paged.removeItem((it) => it.id === id)
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  const getBill = useCallback(
    async (id: string): Promise<{ ok: true; data: BillDownload } | { ok: false; message: string }> => {
      const url = fillPath(APIROUTES.MANAGEPURCHASEBILL, { id })
      const res = await authedFetch(url)
      if (!res.ok) {
        const { message } = await parseApiError(res)
        return { ok: false, message }
      }
      const body = (await res.json()) as { success: true; data: BillDownload }
      return { ok: true, data: body.data }
    },
    [authedFetch]
  )

  const uploadBill = useCallback(
    async (id: string, input: BillUploadInput) => {
      const r = await mutateJson<ManagePurchaseDto>(
        authedFetch,
        fillPath(APIROUTES.MANAGEPURCHASEBILL, { id }),
        'PUT',
        input
      )
      if (!r.ok) return r
      paged.updateItem((it) => it.id === id, r.data)
      return { ok: true as const, data: r.data }
    },
    [authedFetch, paged]
  )

  const deleteBill = useCallback(
    async (id: string) => {
      const r = await mutateJson<ManagePurchaseDto>(
        authedFetch,
        fillPath(APIROUTES.MANAGEPURCHASEBILL, { id }),
        'DELETE'
      )
      if (!r.ok) return { ok: false as const, message: r.message }
      paged.updateItem((it) => it.id === id, r.data)
      return { ok: true as const, data: r.data }
    },
    [authedFetch, paged]
  )

  const extras = paged.extras ?? EMPTY_EXTRAS
  return {
    items: paged.items,
    total: paged.total,
    page: paged.page,
    pageSize: paged.pageSize,
    isLoading: paged.isLoading,
    error: paged.error,
    currentGstCgstBps: extras.currentGstCgstBps,
    currentGstSgstBps: extras.currentGstSgstBps,
    units: extras.units,
    suppliers: extras.suppliers,
    setPage: paged.setPage,
    reload: paged.reload,
    create,
    update,
    remove,
    getBill,
    uploadBill,
    deleteBill
  }
}
